import { AddOutlined, KeyboardArrowRightOutlined } from "@mui/icons-material";
import { Box, Chip, Collapse, IconButton, Tooltip } from "@mui/material";
import { green, grey, orange, red } from "@mui/material/colors";
import { every, size, some } from "lodash";
import groupBy from "lodash/groupBy";
import { useMemo, useState } from "react";
import { ChecklistSectionCard } from "../../../../../components/common/Loan/LoanChecklist/ui/cards/ChecklistSectionCard";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";

import { useSelector } from "react-redux";
import CheckboxInput from "../../../../../_src_shared/components/inputs/CheckboxInput.js";
import FlxGrid from "../../../../../_src_shared/components/layout/FlxGrid.js";
import LoanFilesDisplayTable from "../../../../../components/common/Loan/tables/LoanFilesDisplayTable.js";
import UploadLoanFileModal from "../../../../../components/modals/common/UploadLoanFileModal.js";
import Htag from "../../../../../components/typography/Htag.jsx";
import { useFileDocGroupAndTypesHook } from "../../../../../hooks/useFileDocGroupAndTypesHook.js";

/**
 * EXAMPLE FILE OBJECT
 {
    "isFinal": false,
    "documentStatus": "New Upload",
    "parsed": "False",
    "searchIndexed": "False",
    "docGroup": "Borrower Documents",
    "docType": "Background Report",
    "file_name_as_loaded": "drivers license front.jpg",
    "file_display_name": "background report testt.jpg",
    "content_type": "image/jpeg",
    "size": 253511,
    "uploadDate": "2025-11-24T20:00:00.518889",
    "modifiedDate": "2025-11-25T16:20:38",
    "reportEffectiveDate": "2025-11-19T05:00:00.000Z",
    "name": "drivers license front.jpg",
    "file_extension": "jpg"
 }
 */

// Returns required doc groups -> doc types based on loanPurpose + loanProductType.
// Mirrors the Python rules you shared (base requirements + purpose/product conditionals).

// Assumes you have fileDocGroupAndTypes in scope (your big mapping object).

const getRequiredDocGroupsAndTypes = (loan, docGroupsAndTypes) => {
  const loanPurpose = loan?.loanPurpose; // 'Purchase' or something else
  const loanProductType = loan?.loanProductType; // e.g. 'Fix and Flip'
  const subjectProperties = loan?.subjectProperties || [];

  // Helper: find which group a doc type belongs to based on fileDocGroupAndTypes
  const findGroupForType = (docType) => {
    for (const [group, types] of Object.entries(docGroupsAndTypes)) {
      if (types.includes(docType)) return group;
    }
    return null;
  };

  // Collect required types (set to avoid dupes)
  const requiredTypes = new Set();

  // ---- Always-required docs per all_loan_required_docs ----
  requiredTypes.add("Bank Statement");

  // minimum_property_docs
  requiredTypes.add("Appraisal PDF");
  requiredTypes.add("Preliminary Title Policy");

  // minimum_entity_docs
  requiredTypes.add("Articles of Incorporation");
  requiredTypes.add("Articles of Organization");
  requiredTypes.add("COGS");
  requiredTypes.add("EIN");
  requiredTypes.add("Operating Agreement");

  // minimum_borrower_docs
  requiredTypes.add("Background Report");
  requiredTypes.add("Borrower Experience");
  requiredTypes.add("Borrower OFAC Report");
  requiredTypes.add("Credit Report");
  requiredTypes.add("Loan Application");

  // Borrower Identification requirement is satisfied by either Photo ID or Passport in Python
  requiredTypes.add("Passport");
  requiredTypes.add("Photo Identification");

  // ---- Purpose-specific docs ----
  if (loanPurpose === "Purchase") {
    requiredTypes.add("Purchase Contract");
  } else {
    requiredTypes.add("Payoff Letter");
    requiredTypes.add("Verification of Mortgage");
  }

  // ---- Product-specific docs (fix_and_flip_required_docs) ----
  const isFixAndFlip =
    typeof loanProductType === "string" &&
    loanProductType.toLowerCase().includes("fix") &&
    loanProductType.toLowerCase().includes("flip");

  const feasibilityRequired = subjectProperties.some(
    (sp) => sp?.feasibility_required,
  );

  if (isFixAndFlip && feasibilityRequired) {
    requiredTypes.add("Budget");
    requiredTypes.add("Feasibility Report");
  }

  // ---- Build group -> types[] structure ----
  const requiredByGroup = {};

  [...requiredTypes].forEach((docType) => {
    let group = findGroupForType(docType);

    // Fallback grouping for types not present in the master map
    if (!group) {
      if (docType === "Borrower Experience") group = "Borrower Documents";
      else if (docType === "Articles of Incorporation") group = "Entity Docs";
      else if (docType === "Budget" || docType === "Feasibility Report")
        group = "Third Party Reports";
      else group = "Borrower Documents"; // safe default
    }

    if (!requiredByGroup[group]) requiredByGroup[group] = [];
    requiredByGroup[group].push(docType);
  });

  // ---- Alphabetize doc types within each group ----
  for (const group of Object.keys(requiredByGroup)) {
    requiredByGroup[group] = requiredByGroup[group]
      .filter((v, i, a) => a.indexOf(v) === i) // de-dupe just in case
      .sort((a, b) => a.localeCompare(b));
  }

  // ---- Alphabetize groups (keys) in final returned object ----
  const sortedRequiredByGroup = Object.keys(requiredByGroup)
    .sort((a, b) => a.localeCompare(b))
    .reduce((acc, group) => {
      acc[group] = requiredByGroup[group];
      return acc;
    }, {});

  return sortedRequiredByGroup;
};

const LoanRequiredDocsPanel = ({ files = [] }) => {
  const { fileDocGroupAndTypes } = useFileDocGroupAndTypesHook();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const [showAll, setShowAll] = useState(false);
  const rootStyles = useMemo(() => {
    return {
      p: 2,
      borderBottom: `1px solid ${grey[300]}`,
    };
  }, []);

  // Index files by docGroup -> docType for quick lookup
  const filesByGroupThenType = useMemo(() => {
    const byGroup = groupBy(files, (f) => f.docGroup || "Unknown");
    const result = {};
    Object.keys(byGroup).forEach((group) => {
      result[group] = groupBy(byGroup[group], (f) => f.docType || "Unknown");
    });
    return result;
  }, [files]);

  const docGroupsAndTypes = useMemo(() => {
    if (showAll) {
      return fileDocGroupAndTypes;
    }

    // return requiredFileDocGroupAndTypes;
    return getRequiredDocGroupsAndTypes(loanDrilldown, fileDocGroupAndTypes);
  }, [showAll, loanDrilldown, fileDocGroupAndTypes]);

  return (
    // <FlxGrid width="500px" sx={{ p: 2 }}>
    <Flx column>
      <Flx fw jb ac sx={rootStyles}>
        <Flx column g={0.5}>
          <Htag variant="h3">Loan Document Checklist (Beta)</Htag>
          <Txt secondary>
            This checklist is an early (beta) version. It’s usable today, but
            the functionality and the items shown are likely to change over
            time.
          </Txt>
        </Flx>
        <CheckboxInput
          label={"Show All Possible Document Types"}
          checked={showAll}
          onChange={(e) => setShowAll(e)}
        />
      </Flx>
      <FlxGrid width="420px" sx={{ p: 2 }} gap={1.5}>
        {Object.entries(docGroupsAndTypes).map(([docGroup, docTypes]) => (
          <DocGroupSection
            key={docGroup}
            docGroup={docGroup}
            docTypes={docTypes}
            filesByType={filesByGroupThenType[docGroup] || {}}
          />
        ))}
      </FlxGrid>
    </Flx>
  );
};

const DocGroupSection = ({ docGroup, docTypes, filesByType }) => {
  const [show, setShow] = useState(false);

  const completedCount = useMemo(
    () => docTypes.filter((dt) => (filesByType[dt] || []).length > 0).length,
    [docTypes, filesByType],
  );

  return (
    <ChecklistSectionCard sx={{ mb: 1 }}>
      <CollapsibleHeader
        show={show}
        setShow={setShow}
        title={
          <Flx ac gap={1.5}>
            {docGroup}
            <DocGroupProgressChip
              completedCount={completedCount}
              totalCount={docTypes.length}
            />
          </Flx>
        }
        endContent={
          <Flx ac g={1}>
            <DocGroupStatusChip docTypes={docTypes} filesByType={filesByType} />
          </Flx>
        }
      />
      <Collapse in={show}>
        <Box sx={{ p: 1.5 }}>
          <RequiredDocTypesList
            docGroup={docGroup}
            docTypes={docTypes}
            filesByType={filesByType}
          />
        </Box>
      </Collapse>
    </ChecklistSectionCard>
  );
};

const DocGroupStatusChip = ({ docTypes, filesByType }) => {
  // For each required docType, get its files array (or [])
  const filesPerType = docTypes.map((dt) => filesByType[dt] || []);

  const anyMissingType = some(filesPerType, (arr) => size(arr) === 0);

  if (anyMissingType) {
    return (
      <Chip
        size="small"
        label="Incomplete"
        sx={{
          background: red[50],
          color: red[800],
          fontWeight: 600,
        }}
      />
    );
  }

  // At this point: every docType has >= 1 file
  const allAcceptedAcrossTypes = every(filesPerType, (arr) =>
    every(arr, (f) => f?.documentStatus === "Accepted"),
  );

  const label = allAcceptedAcrossTypes
    ? "Complete"
    : "Uploaded & Awaiting Approval";

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        background: green[50],
        color: green[800],
        fontWeight: 600,
      }}
    />
  );
};

const DocGroupProgressChip = ({ completedCount, totalCount }) => {
  return (
    <Tooltip
      title={`${completedCount} of ${totalCount} document types received`}
    >
      <Chip
        label={`${completedCount} / ${totalCount}`}
        color={completedCount === totalCount ? "success" : "inherit"}
        variant={completedCount === totalCount ? "filled" : "outlined"}
        size="small"
        sx={{
          fontSize: "12px",
          minWidth: 24,
          flexGrow: 1,
        }}
      />
    </Tooltip>
  );
};

const RequiredDocTypesList = ({ docGroup, docTypes, filesByType }) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {docTypes.map((docType) => (
        <DocTypeFilesSection
          key={docType}
          docType={docType}
          docGroup={docGroup}
          filesForType={filesByType[docType] || []}
        />
      ))}
    </Box>
  );
};

const DocTypeFilesSection = ({ docGroup, docType, filesForType }) => {
  const [show, setShow] = useState(false);
  const hasFiles = filesForType?.length > 0;

  return (
    <Box
      sx={{
        border: `1px solid ${grey[300]}`,
        borderRadius: 1.5,
        background: "#ffffff",
      }}
    >
      <CollapsibleHeader
        show={show}
        disabled={!hasFiles}
        setShow={setShow}
        size="small"
        // titleSx={{ fontSize: "13px", fontWeight: 400 }}
        title={
          <Flx ac gap={1}>
            {docType}
            <DocTypeFilesSectionChip
              filesForType={filesForType}
              hasFiles={hasFiles}
            />
          </Flx>
        }
        endContent={
          <FileUploaderIconButton
            tooltip={`Upload ${docType}`}
            docGroup={docGroup}
            docType={docType}
          />
        }
      />
      {hasFiles && show && (
        <LoanFilesDisplayTable view="compact" rowData={filesForType} />
      )}
    </Box>
  );
};

const DocTypeFilesSectionChip = ({ filesForType, hasFiles }) => {
  if (!hasFiles) {
    return (
      <Chip
        size="small"
        label="Missing"
        sx={{
          background: red[50],
          color: red[800],
          fontWeight: 500,
        }}
      />
    );
  }

  const allAccepted = every(
    filesForType,
    (f) => f?.documentStatus === "Accepted",
  );

  const label = allAccepted
    ? `Received & Accepted (${size(filesForType)})`
    : `Received (${size(filesForType)})`;

  const bg = allAccepted ? green[50] : orange[50];
  const fg = allAccepted ? green[800] : orange[900];

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        background: bg,
        color: fg,
        fontWeight: 500,
      }}
    />
  );
};

const CollapsibleHeader = ({
  show,
  setShow,
  title,
  titleSx,
  endContent,
  disabled,
  size = "medium",
  sx,
}) => {
  const iconSizeMap = {
    small: "18px",
    medium: "18px",
    large: "18px",
  };
  const titleSizeMap = {
    small: "13px",
    medium: "15px",
    large: "18px",
  };
  const titleWeighteMap = {
    small: 500,
    medium: 600,
    large: 900,
  };
  return (
    <Flx
      fw
      jb
      ac
      sx={{
        // px: 1.5,
        // py: 2,
        //
        pr: 1.5,
        borderBottom: `1px solid ${grey[200]}`,
        ...sx,
      }}
    >
      <Box
        onClick={() => !disabled && setShow((s) => !s)}
        sx={{
          flexGrow: 1,
          cursor: disabled ? "default" : "pointer",
          pl: 1.5,
          py: 2,
        }}
      >
        <Flx ac gap={0.5}>
          <KeyboardArrowRightOutlined
            // fontSize="small"
            className="thin"
            sx={{
              fontSize: iconSizeMap[size],
              opacity: disabled ? 0.45 : 1,
              transition: "transform 0.3s",
              transform: show ? "rotate(90deg)" : "rotate(0deg)",
            }}
          />

          <Txt
            sx={{
              fontSize: titleSizeMap[size],
              fontWeight: titleWeighteMap[size],
              ...titleSx,
            }}
          >
            {title}
          </Txt>
        </Flx>
      </Box>
      {endContent}
    </Flx>
  );
};

const FileUploaderIconButton = ({ docGroup, docType }) => {
  const [addFiles, setAddFiles] = useState(false);
  return (
    <>
      <Tooltip title={`Upload ${docType}`}>
        <IconButton onClick={() => setAddFiles(true)} size="small">
          <AddOutlined />
        </IconButton>
      </Tooltip>
      {addFiles ? (
        <UploadLoanFileModal
          show={addFiles}
          onClose={setAddFiles}
          docGroup={docGroup}
          docType={docType}
        />
      ) : null}
    </>
  );
};

export default LoanRequiredDocsPanel;
